import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { usePermissionsStore } from '@/stores/permissions'
import { matchesPermission, PERMISSIONS } from '@/utils/permissions'

export function usePermissions() {
  const permissionsStore = usePermissionsStore()
  const { permissions } = storeToRefs(permissionsStore)

  const hasPermission = (permissionName: string) =>
    matchesPermission(permissions.value, permissionName)
  
  // Check multiple permissions (AND)
  const hasAllPermissions = (permissionNames: string[]) => {
    return permissionNames.every(p => hasPermission(p))
  }

  // Check multiple permissions (OR)
  const hasAnyPermission = (permissionNames: string[]) => {
    return permissionNames.some(p => hasPermission(p))
  }

  // Helper for common actions (canView, canCreate, etc.)
  // These map standard suffixes to permission checks
  const canView = (resource: string) => hasPermission(`${resource}:read`) || hasPermission(resource)
  const canCreate = (resource: string) => hasPermission(`${resource}:create`)
  const canEdit = (resource: string) => hasPermission(`${resource}:update`) || hasPermission(`${resource}:edit`)
  const canDelete = (resource: string) => hasPermission(`${resource}:delete`)
  
  return {
    permissions: computed(() => permissionsStore.permissions),
    loading: computed(() => permissionsStore.loading),
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    PERMISSIONS // Re-export constants
  }
}
